/**
 * Typed input of the shared time / date pickers (common/timePicker.vue, common/datePicker.vue).
 * Typed values are applied at once when complete (and on blur / Enter), not only after the debounce.
 */

/**
 * "16:20", "16.20", "16,20", "9:05", "1620", "905", "16" (full hour) -> "HH:mm"; null when not a time of day.
 * Minutes are not rounded here.
 */
export function parseTypedTime(text: string | null | undefined): string | null {
    const value = (text || '').trim().replace(/\s*(uhr|h)$/i, '');
    let hour: number;
    let minute: number;
    let match = /^(\d{1,2})[:.,](\d{1,2})$/.exec(value);
    if (match) {
        hour = Number(match[1]);
        minute = Number(match[2]);
    } else if ((match = /^(\d{1,2})(\d{2})$/.exec(value))) {
        hour = Number(match[1]);
        minute = Number(match[2]);
    } else if ((match = /^(\d{1,2})$/.exec(value))) {
        hour = Number(match[1]);
        minute = 0;
    } else {
        return null;
    }
    if (hour > 23 || minute > 59) {
        return null;
    }
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Typing is finished (apply at once without waiting for the debounce): "16:20", "9:05", "16.20", "1620".
 * Shorter digit-only input ("162") may still grow, so it waits for blur / Enter / the debounce.
 */
export function isCompleteTypedTime(text: string | null | undefined): boolean {
    const value = (text || '').trim();
    return /^\d{1,2}[:.,]\d{2}$/.test(value) || /^\d{4}$/.test(value);
}

/** Round the minutes to a step (5 for the time picker); 23:58 stays on the same day (23:55) */
export function roundTypedTime(time: string, step = 5): string {
    const [hour, minute] = time.split(':').map(Number);
    let rounded = Math.round(minute / step) * step;
    let h = hour;
    if (rounded >= 60) {
        if (hour < 23) {
            h = hour + 1;
            rounded = 0;
        } else {
            rounded = 60 - step;
        }
    }
    return `${String(h).padStart(2, '0')}:${String(rounded).padStart(2, '0')}`;
}

/** A complete German date "D.M.YYYY" / "DD.MM.YYYY" (apply at once) */
export function isCompleteTypedDate(text: string | null | undefined): boolean {
    return /^\d{1,2}\.\d{1,2}\.\d{4}$/.test((text || '').trim());
}
